import { useCameraSwitch } from "@features/camera-preview";
import { ConnectionQuality } from "livekit-client";
import type { Call, LinkStats } from "@entities/call";
import type { Uuid } from "@shared/api";
import { Permission, has } from "@entities/role";
import { ShareButton } from "@features/screen-share";
import { SoundboardButton } from "@features/soundboard";
import { useStore } from "@features/chat";
import { Hint } from "@shared/ui/bits";
import { Popover, PopoverContent, PopoverTrigger } from "@shared/ui/popover";
import {
  IconCamera,
  IconLeaveCall,
  IconLinkHigh,
  IconLinkLow,
  IconLinkMedium,
  IconLinkNone,
  IconVoiceChannel,
} from "@shared/ui/icons";

/**
 * Панель идущего звонка — над своей строкой, в левой колонке.
 *
 * Раньше это была полоса во всю ширину внизу экрана. Место выбрано заново по
 * той же причине, по какой там же стоят выключатели звука: связь, свой
 * микрофон и своё имя — это одна кучка сведений о себе, и искать её глазами
 * человек должен в одном углу, а не в двух.
 *
 * Видна всегда, пока идёт разговор, — в том числе когда открыт сам голосовой
 * канал и его полоса с теми же кнопками стоит рядом. Повтор тут не помеха:
 * место у панели одно и то же в любом канале, и именно этим она и полезна —
 * не надо вспоминать, где сейчас лежит трубка.
 *
 * Звук от неё не зависит вовсе: он живёт отдельно, в `CallAudio`.
 */
export function CallDock({
  call,
  channelName,
  serverName,
  permissions,
  serverId,
  onOpen,
}: {
  call: Call;
  channelName: string;
  serverName: string;
  /** Сервер звонка — его звуки и на панели. */
  serverId: Uuid | null;
  /** Права в канале звонка, а не в открытом сейчас: смотреть можно другой. */
  permissions: number;
  onOpen: () => void;
}) {
  // До раннего выхода: порядок хуков не должен зависеть от состояния звонка.
  const camera = useCameraSwitch(call);
  const { servers } = useStore();
  if (call.state !== "live" || !call.channelId) return null;

  const canVideo = has(permissions, Permission.Video);
  const tone = toneOf(call.quality);

  return (
    <div className="call-dock glass-host">
      <div className="call-dock-head">
        <Popover>
          <Hint label="Подробности связи">
            <PopoverTrigger asChild>
              <button type="button" className={`call-dock-link ${tone}`}>
                <Bars quality={call.quality} />
                <span className="call-dock-state">{stateOf(call.quality)}</span>
              </button>
            </PopoverTrigger>
          </Hint>

          <PopoverContent side="top" align="start" sideOffset={8} aria-label="Показатели связи">
            <Numbers link={call.link} sending={call.microphone} />
          </PopoverContent>
        </Popover>

        <Hint label="Выйти из звонка">
          <button type="button" className="call-dock-hang" onClick={() => void call.leave()}>
            <IconLeaveCall size={15} />
          </button>
        </Hint>
      </div>

      {/* Куда именно попали: канал и сервер. Нажатие открывает его же. */}
      <button type="button" className="call-dock-where" onClick={onOpen}>
        <IconVoiceChannel size={12} />
        <span className="call-dock-channel ellipsis">{channelName}</span>
        <span className="call-dock-server ellipsis">/ {serverName}</span>
      </button>

      <div className="call-dock-acts">
        {canVideo && (
          <>
            <Hint label={call.camera ? "Выключить камеру" : "Включить камеру"}>
              <button
                type="button"
                className={`dock-act ${call.camera ? "on" : ""}`}
                onClick={camera.toggle}
              >
                <IconCamera size={15} />
              </button>
            </Hint>
            {camera.preview}

            <ShareButton
              call={call}
              channelName={channelName}
              serverName={serverName}
              variant="dock"
              side="top"
            />
          </>
        )}

        {serverId && (
          <SoundboardButton
            serverId={serverId}
            servers={servers}
            channelId={call.channelId}
            canPlay={has(permissions, Permission.Speak) && has(permissions, Permission.UseSoundboard)}
            canExternal={has(permissions, Permission.UseExternalSounds)}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Столбики связи.
 *
 * Пока сервер не сказал ничего, показываются полными: связь есть — мы в
 * канале, — а рисовать «ноль столбиков» до первой оценки значило бы пугать
 * человека каждым входом.
 */
function Bars({ quality }: { quality: ConnectionQuality | null }) {
  if (quality === ConnectionQuality.Lost) return <IconLinkNone size={15} />;
  if (quality === ConnectionQuality.Poor) return <IconLinkLow size={15} />;
  if (quality === ConnectionQuality.Good) return <IconLinkMedium size={15} />;
  return <IconLinkHigh size={15} />;
}

function stateOf(quality: ConnectionQuality | null): string {
  switch (quality) {
    case ConnectionQuality.Lost:
      return "Связь потеряна";
    case ConnectionQuality.Poor:
      return "Связь плохая";
    case ConnectionQuality.Good:
      return "Связь неровная";
    default:
      return "Связь устойчивая";
  }
}

function toneOf(quality: ConnectionQuality | null): string {
  if (quality === ConnectionQuality.Lost || quality === ConnectionQuality.Poor) return "bad";
  if (quality === ConnectionQuality.Good) return "warn";
  return "ok";
}

/*
 * Дробь с запятой, а не с точкой: числа читает человек, а не машина.
 * Задержка при этом целая — десятые доли миллисекунды не значат ничего и
 * только дёргаются каждую секунду.
 */
const ms = new Intl.NumberFormat("ru", { maximumFractionDigits: 0 });
const share = new Intl.NumberFormat("ru", { maximumFractionDigits: 1 });

/**
 * Числа под оценкой.
 *
 * Пусто и «ноль» — разные ответы, и второй здесь запрещён: ноль потерь
 * значит, что мы измерили и не потеряли, а прочерк — что мерить было нечем.
 * Молчащему не из чего считать долю потерянного: считает её получатель по
 * нашим же пакетам, а пакетов нет.
 */
function Numbers({ link, sending }: { link: LinkStats | null; sending: boolean }) {
  return (
    <div className="call-nums">
      <div className="caption" style={{ padding: "0 0 6px" }}>
        Связь
      </div>

      <Line title="Задержка" value={link?.rttMs == null ? null : `${ms.format(link.rttMs)} мс`} />
      <Line
        title="Средняя за минуту"
        value={link?.avgRttMs == null ? null : `${ms.format(link.avgRttMs)} мс`}
      />
      <Line
        title="Потеря исходящих"
        value={link?.lossPercent == null ? null : `${share.format(link.lossPercent)} %`}
      />

      <div className="call-picker-note" style={{ padding: "8px 0 0" }}>
        Считается до сервера и обратно, а не до собеседника: у него дорога своя
        и числа свои. От 250 мс голос начинает отставать, от 10 % потерь —
        сыпаться.
        {!sending && " Пока микрофон выключен, терять нечего — потерь и нет."}
      </div>
    </div>
  );
}

function Line({ title, value }: { title: string; value: string | null }) {
  return (
    <div className="call-nums-row">
      <span className="grow">{title}</span>
      <span className={value === null ? "muted" : "call-nums-value"}>{value ?? "нет данных"}</span>
    </div>
  );
}
